import { ApiBaseService, ApiV2Service } from './api.base.service';
import store from '@/store/store';
import { IdResponse, SigfoxApiCredentials, SuccessResponse, LoRaNetworkServer } from '@/types';

export interface SigfoxCredentials {
  id: number;
  login: string;
  sigfox_group: string;
  default_contract_id: string;
}

export interface LoRaCredentials {
  id: number;
  lns: string;
  username?: string;
  password?: string;
  customerId?: string;
  clusterId?: string;
  organisation_id?: string;
  application_server?: string;
  account_token?: string;
  application_id?: string;
  api_key?: string;
}

export class ConnectivityService {
  private api: ApiBaseService;

  constructor() {
    this.api = ApiV2Service;
  }

  public async getSigfoxApiCredentials(): Promise<SigfoxApiCredentials | null> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get<SigfoxApiCredentials | null>(`organisation/${organisationId}/network/sigfox/api`, { noToastFilters: [ { status: 404, gives: (r: any) => null }] });
  }

  public async createSigfoxApiCredentials(credentials: SigfoxApiCredentials): Promise<IdResponse> {
    const body = {
      managed: false,
      ...credentials,
    };
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisationId}/network/sigfox/api`, body  );
  }

  public async updateSigfoxApiCredentials(sigfoxApiId: string, credentials: SigfoxApiCredentials): Promise<IdResponse> {
    const body = {
      managed: false,
      ...credentials,
    };
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisationId}/network/sigfox/api/${sigfoxApiId}`, body);
  }

  public async deleteSigfoxApiCredentials(sigfoxApiId: string): Promise<SuccessResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.delete(`organisation/${organisationId}/network/sigfox/api/${sigfoxApiId}`);
  }

  public async getLoriotSettings(): Promise<LoRaCredentials | null> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/network/lora`, { noToastFilters: [ { status: 404, gives: (r: any) => null }]});
  }

  public async hasTTISettings(): Promise<{result: boolean}> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/network/hasTTISettings`);
  }

  public async getLoRaNetworkServers(): Promise<LoRaNetworkServer[]> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/network/lora/servers`);
  }

  public async createLoRaSetting( lns: string): Promise<IdResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    const body = {lns};
    return this.api.post(`organisation/${organisationId}/network/lora/other`, body);
  }

  public async createNetmoreSettings(body: { username: string; password: string; service_provider_code: string }): Promise<IdResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisationId}/network/lora/netmore`, body);
  }
  public async createIoTScotlandSettings( lns: string, username?: string, password?: string, customerId?: string, clusterId?: string): Promise<IdResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    const cust = customerId ? Number(customerId) : undefined;
    const clust = clusterId ? Number(clusterId) : undefined;
    const body = {lns, username, password, customer_id: cust, cluster_id: clust};
    return this.api.post(`organisation/${organisationId}/network/lora/iotscotland`, body);
  }

  public async createLoRaTTSSettings( lns: string, applicationServer: string, apiKey: string, organisationId: string, applicationId?: string): Promise<IdResponse> {
    const orgId = store.getters.activeOrganisation[0].id;
    const body = {lns, application_id: applicationId, application_server: applicationServer, organisation_id: organisationId, api_key: apiKey};
    return this.api.post(`organisation/${orgId}/network/lora/tts`, body);
  }

  public async createLoriotSettings( settings: { host: string; application_id?: string; api_key: string, lns?: string} ): Promise<IdResponse> {
    const orgId = store.getters.activeOrganisation[0].id;
    settings.lns = 'loriot_managed';
    return this.api.post(`organisation/${orgId}/network/lora/loriot`, settings);
  }

  public async updateLoRaSettings( lns: string, id: number, username?: string, password?: string): Promise<IdResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    const body = {lns, username, password};
    return this.api.put(`organisation/${organisationId}/network/lora/iotscotland`, body);
  }

  public async updateTTISettings( lns: string,  apiKey: string ): Promise<IdResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    const body = {lns, api_key: apiKey};
    return this.api.put(`organisation/${organisationId}/network/lora/tti`, body);
  }

  public async hasNetworkSettings(setting: string): Promise<{result: boolean}> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/network/hasNetworkSettings?network=${setting}`);
  }
}

export default new ConnectivityService();
